import React, { useState, useEffect } from "react";
import openSocket from "../../services/socket-io";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Container from "@material-ui/core/Container";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import Chip from "@material-ui/core/Chip";
import Box from "@material-ui/core/Box";
import Divider from "@material-ui/core/Divider";
import Grid from "@material-ui/core/Grid";
import { toast } from "react-toastify";

import api from "../../services/api";
import { i18n } from "../../translate/i18n.js";
import toastError from "../../errors/toastError";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";

// ============================================================
// [ZONA BLINDADA - Trae AI 2026-03-11 - PROHIBIDO MODIFICAR]
// Página de configuración de Gemini. Muestra el estado de la
// API Key, modelo activo, parámetros y estimación de costos.
// ============================================================

const ACTIVE_MODEL = "gemini-2.5-flash";
const MODEL_PARAMS = {
  temperature: 0.7,
  topP: 0.95,
  maxOutputTokens: 1500,
};
const PRICING = {
  inputPerMillion: 0.15,   // USD por 1M tokens de entrada (Gemini 2.5 Flash estimado)
  outputPerMillion: 0.60,  // USD por 1M tokens de salida
  audioCostPerMin: 0.006,  // USD por minuto de audio
};

const useStyles = makeStyles(theme => ({
  sectionPaper: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
    borderRadius: 12,
  },
  sectionTitle: {
    fontWeight: 700,
    marginBottom: theme.spacing(1.5),
    color: theme.palette.primary.main,
    textTransform: "uppercase",
    fontSize: "0.75rem",
    letterSpacing: 1,
  },
  modelBadge: {
    backgroundColor: "#1a1a2e",
    color: "#00d4ff",
    fontFamily: "monospace",
    fontWeight: 700,
    fontSize: "0.85rem",
    padding: "4px 12px",
    borderRadius: 6,
    display: "inline-block",
    marginBottom: theme.spacing(1),
  },
  paramRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: "4px 0",
    borderBottom: "1px solid " + theme.palette.divider,
  },
  paramLabel: {
    color: theme.palette.text.secondary,
    fontSize: "0.82rem",
  },
  paramValue: {
    fontWeight: 600,
    fontSize: "0.82rem",
    color: theme.palette.text.primary,
  },
  costHighlight: {
    backgroundColor: "#f0fdf4",
    border: "1px solid #bbf7d0",
    borderRadius: 8,
    padding: theme.spacing(1.5),
    marginTop: theme.spacing(1),
  },
  costBig: {
    fontWeight: 800,
    fontSize: "1.4rem",
    color: "#16a34a",
  },
  costLabel: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
  },
  statusRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: theme.spacing(2),
  },
  saveButton: {
    marginTop: theme.spacing(1),
    borderRadius: 8,
    fontWeight: 700,
  },
}));

// Calcula el costo estimado por cliente
function calcCosto() {
  const contextTokens = 7250;
  const turns = 18;
  const outputTokensPerTurn = 150;
  const audioMins = 1.5;

  const inputCost = (contextTokens * turns / 1_000_000) * PRICING.inputPerMillion;
  const outputCost = (outputTokensPerTurn * turns / 1_000_000) * PRICING.outputPerMillion;
  const audioCost = audioMins * PRICING.audioCostPerMin;
  const total = inputCost + outputCost + audioCost;
  return {
    total: total.toFixed(4),
    cop: Math.round(total * 4250),
    daily100: (total * 100).toFixed(2),
    daily100cop: Math.round(total * 100 * 4250),
    monthly3000: (total * 3000).toFixed(2),
    monthly3000cop: Math.round(total * 3000 * 4250),
  };
}

const GeminiSettings = () => {
	const classes = useStyles();
	const [settings, setSettings] = useState([]);
	const [newGeminiKey, setNewGeminiKey] = useState("");
	const [geminiConnected, setGeminiConnected] = useState(false);
  const cost = calcCosto();

	useEffect(() => {
		const fetchSession = async () => {
			try {
				const { data } = await api.get("/settings");
				setSettings(data);
			} catch (err) {
				toastError(err);
			}
		};
		fetchSession();
	}, []);

	useEffect(() => {
		api.get("/settings/gemini-check").then(({ data }) => {
			setGeminiConnected(data.isConnected);
		}).catch(err => console.error(err));
	}, []);

	useEffect(() => {
		const socket = openSocket();
		socket.on("settings", data => {
			if (data.action === "update") {
                setSettings(prevState => {
					const aux = [...prevState];
					const settingIndex = aux.findIndex(s => s.key === data.setting.key);
                    if (settingIndex !== -1) {
					    aux[settingIndex].value = data.setting.value;
                    }
					return aux;
				});

				if (data.setting.key === "geminiApiKey") {
					api.get("/settings/gemini-check").then(({ data }) => {
						setGeminiConnected(data.isConnected);
					}).catch(err => console.error(err));
				}
			}
		});
		return () => socket.disconnect();
	}, []);

	const handleSaveGemini = async () => {
		if (!newGeminiKey) return;
		try {
			await api.put(`/settings/geminiApiKey`, {
				value: newGeminiKey,
			});
			toast.success(i18n.t("settings.success"));
			setNewGeminiKey("");
			const { data } = await api.get("/settings/gemini-check");
			setGeminiConnected(data.isConnected);
		} catch (err) {
			toastError(err);
		}
	};

	const geminiKey = Array.isArray(settings) ? settings.find(s => s.key === "geminiApiKey")?.value : null;
	const isGeminiConfigured = !!geminiKey;
	const maskedKey = geminiKey ? `••••••••••••••••••••${geminiKey.slice(-6)}` : "No configurada";

	return (
		<MainContainer>
      <MainHeader>
        <Title>⚙️ Configuración de Gemini AI</Title>
      </MainHeader>
			<Container maxWidth="sm">

        {/* --- ESTADO DE CONEXIÓN --- */}
				<Paper className={classes.sectionPaper} elevation={2}>
          <Typography className={classes.sectionTitle}>🔑 API Key</Typography>
          <div className={classes.statusRow}>
            <Typography variant="body2" style={{ color: "#555" }}>
              {isGeminiConfigured ? maskedKey : "Sin configurar"}
            </Typography>
            <Chip
              label={`${geminiConnected ? "✅ Conectado" : "❌ Desconectado"}`}
              style={{
                backgroundColor: geminiConnected ? "#dcfce7" : "#fee2e2",
                color: geminiConnected ? "#166534" : "#991b1b",
                fontWeight: 700,
                fontSize: "0.75rem",
              }}
            />
          </div>
          <TextField
            id="gemini-api-key"
            label="Nueva API Key de Gemini"
            placeholder="AIza..."
            variant="outlined"
            fullWidth
            margin="dense"
            value={newGeminiKey}
            onChange={(e) => setNewGeminiKey(e.target.value)}
          />
          <Button
            variant="contained"
            color="primary"
            fullWidth
            className={classes.saveButton}
            onClick={handleSaveGemini}
          >
            Guardar API Key
          </Button>
        </Paper>

        {/* --- MODELO ACTIVO --- */}
        <Paper className={classes.sectionPaper} elevation={2}>
          <Typography className={classes.sectionTitle}>🤖 Modelo Activo</Typography>
          <div className={classes.modelBadge}>{ACTIVE_MODEL}</div>
          <Divider style={{ margin: "12px 0" }} />
          <div className={classes.paramRow}>
            <span className={classes.paramLabel}>Temperatura</span>
            <span className={classes.paramValue}>{MODEL_PARAMS.temperature}</span>
          </div>
          <div className={classes.paramRow}>
            <span className={classes.paramLabel}>Top-P</span>
            <span className={classes.paramValue}>{MODEL_PARAMS.topP}</span>
          </div>
          <div className={classes.paramRow}>
            <span className={classes.paramLabel}>Max Output Tokens</span>
            <span className={classes.paramValue}>{MODEL_PARAMS.maxOutputTokens}</span>
          </div>
          <div className={classes.paramRow}>
            <span className={classes.paramLabel}>Versión API</span>
            <span className={classes.paramValue}>v1beta</span>
          </div>
        </Paper>

        {/* --- ESTIMACIÓN DE COSTOS --- */}
        <Paper className={classes.sectionPaper} elevation={2}>
          <Typography className={classes.sectionTitle}>💰 Estimación de Costos</Typography>
          <Grid container spacing={1} style={{ marginBottom: 8 }}>
            <Grid item xs={6}>
              <Typography variant="caption" className={classes.paramLabel}>Input</Typography>
              <Typography variant="body2" className={classes.paramValue}>${PRICING.inputPerMillion}/M tokens</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" className={classes.paramLabel}>Output</Typography>
              <Typography variant="body2" className={classes.paramValue}>${PRICING.outputPerMillion}/M tokens</Typography>
            </Grid>
          </Grid>
          <Divider style={{ margin: "8px 0" }} />
          <div className={classes.costHighlight}>
            <Typography className={classes.costLabel}>Por cliente completo (18 mensajes + 3 audios)</Typography>
            <span className={classes.costBig}>${cost.total} USD</span>
            <Typography className={classes.costLabel}>≈ ${cost.cop.toLocaleString()} COP</Typography>
          </div>
          <Box display="flex" justifyContent="space-between" marginTop={1.5}>
            <Box textAlign="center">
              <Typography variant="caption" className={classes.paramLabel}>100 clientes/día</Typography>
              <Typography variant="body2" className={classes.paramValue}>${cost.daily100} USD</Typography>
              <Typography style={{ fontSize: "0.7rem", color: "#888" }}>≈ ${cost.daily100cop.toLocaleString()} COP</Typography>
            </Box>
            <Box textAlign="center">
              <Typography variant="caption" className={classes.paramLabel}>3,000 clientes/mes</Typography>
              <Typography variant="body2" className={classes.paramValue}>${cost.monthly3000} USD</Typography>
              <Typography style={{ fontSize: "0.7rem", color: "#888" }}>≈ ${cost.monthly3000cop.toLocaleString()} COP</Typography>
            </Box>
          </Box>
        </Paper>

			</Container>
		</MainContainer>
	);
};

export default GeminiSettings;
